// Warehouse map from the location tree (owner's idea 2026-10-28, mockup warehouse-map):
// a bay's elevation as seen from the aisle, and a zone plan. Everything is derived from the
// registry: a bin's code, read after its aisle's code, yields bay, shelf and slot numbers
// (`HA-1-2-1` → bay 1, shelf 2, slot 1; two numbers → bay and slot; one → slot). Row
// placement in the plan is alphabetical until a layout says otherwise.

import type { BundleLayoutOut, BundleLocation } from "../api/client.js";

export interface BinPos {
  bin: BundleLocation;
  aisle: BundleLocation | null;
  bay: number;
  shelf: number;
  slot: number;
}

const parentPath = (l: BundleLocation) => (l.path.includes("/") ? l.path.slice(0, l.path.lastIndexOf("/")) : null);

/** Bay, shelf and slot of every bin, grouped by aisle path. */
export function binPositions(locations: readonly BundleLocation[]): Map<string, BinPos[]> {
  const byPath = new Map(locations.map((l) => [l.path, l]));
  const out = new Map<string, BinPos[]>();
  for (const bin of locations) {
    if (bin.kind !== "bin") continue;
    const pp = parentPath(bin);
    const aisle = pp ? byPath.get(pp) ?? null : null;
    const rest = aisle && bin.code.toUpperCase().startsWith(aisle.code.toUpperCase()) ? bin.code.slice(aisle.code.length) : bin.code;
    const nums = rest.split(/[^0-9]+/).filter(Boolean).map(Number);
    const [bay, shelf, slot] = nums.length >= 3 ? [nums[0]!, nums[1]!, nums[2]!] : nums.length === 2 ? [nums[0]!, 1, nums[1]!] : [1, 1, nums[0] ?? 1];
    const key = aisle?.path ?? "";
    out.set(key, [...(out.get(key) ?? []), { bin, aisle, bay, shelf, slot }]);
  }
  return out;
}

/** The bay a bin belongs to: shelves top-down, slots left-right. */
export function bayOf(locations: readonly BundleLocation[], binId: string): { aisle: BundleLocation | null; bay: number; shelves: BinPos[][] } | null {
  for (const list of binPositions(locations).values()) {
    const me = list.find((p) => p.bin.id === binId);
    if (!me) continue;
    const cells = list.filter((p) => p.bay === me.bay);
    const maxShelf = Math.max(...cells.map((c) => c.shelf));
    const shelves: BinPos[][] = [];
    for (let sh = maxShelf; sh >= 1; sh--) shelves.push(cells.filter((c) => c.shelf === sh).sort((a, b) => a.slot - b.slot));
    return { aisle: me.aisle, bay: me.bay, shelves };
  }
  return null;
}

/** The whole rack of an aisle as seen from the aisle: bays left to right, shelves top down, every
 *  bin a cell to click (owner 2026-11-02: from the map one could reach the rack but not its bays,
 *  shelves and bins). */
export function RackElevation({ locations, aislePath, occupied, selectedId, onPick }: { locations: readonly BundleLocation[]; aislePath: string; occupied?: ReadonlySet<string> | undefined; selectedId?: string | null | undefined; onPick: (bin: BundleLocation) => void }) {
  const list = binPositions(locations).get(aislePath) ?? [];
  if (list.length === 0) return null;
  const bays = [...new Set(list.map((p) => p.bay))].sort((a, b) => a - b);
  return (
    <div className="elev-rack" style={{ display: "flex", gap: 18, overflowX: "auto", alignItems: "flex-end", paddingBottom: 4 }}>
      {bays.map((bay) => {
        const cells = list.filter((p) => p.bay === bay);
        const maxShelf = Math.max(...cells.map((c) => c.shelf));
        const shelves: BinPos[][] = [];
        for (let sh = maxShelf; sh >= 1; sh--) shelves.push(cells.filter((c) => c.shelf === sh).sort((a, b) => a.slot - b.slot));
        return (
          <div key={bay} className="elev">
            <div className="et">{bay}</div>
            {shelves.map((row, i) => (
              <div key={i} className="shelf">
                {row.map((c) => <span key={c.bin.id} className={`cell${occupied?.has(c.bin.id) ? " full" : ""}${c.bin.id === selectedId ? " sel" : ""} pick`} onClick={() => onPick(c.bin)} role="button">{c.bin.code}</span>)}
                <em>{shelves.length - i}</em>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** A bay as seen from the aisle. `occupied` marks bins holding something; `target` flashes. */
export function BayElevation({ locations, binId, occupied, compact, onPick }: { locations: readonly BundleLocation[]; binId: string; occupied?: ReadonlySet<string> | undefined; compact?: boolean | undefined; onPick?: ((bin: BundleLocation) => void) | undefined }) {
  const bay = bayOf(locations, binId);
  if (!bay) return null;
  return (
    <div className={`elev${compact ? " compact" : ""}`}>
      <div className="et">{bay.aisle ? `${bay.aisle.code} · ` : ""}bay {bay.bay}</div>
      {bay.shelves.map((cells, i) => (
        <div key={i} className="shelf">
          {cells.map((c) => <span key={c.bin.id} className={`cell${occupied?.has(c.bin.id) ? " full" : ""}${c.bin.id === binId ? " sel" : ""}${onPick ? " pick" : ""}`} onClick={onPick ? () => onPick(c.bin) : undefined} role={onPick ? "button" : undefined}>{compact ? c.slot : c.bin.code}</span>)}
          <em>{bay.shelves.length - i}</em>
        </div>
      ))}
    </div>
  );
}

/** The plan of one zone: its aisles as rows of bays, every slot a cell. Rows in alphabetical
 *  order with an aisle marked between every pair until a layout says otherwise. */
export function ZonePlan({ locations, zonePath, occupied, selectedId, onPick }: { locations: readonly BundleLocation[]; zonePath: string; occupied: ReadonlySet<string>; selectedId: string | null; onPick: (bin: BundleLocation) => void }) {
  const positions = binPositions(locations);
  const aisles = [...positions.entries()].filter(([p]) => p === zonePath || p.startsWith(`${zonePath}/`)).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="plan">
      {aisles.map(([path, list], i) => {
        const bays = [...new Set(list.map((p) => p.bay))].sort((a, b) => a - b);
        return (
          <div key={path}>
            {i > 0 && i % 2 === 0 && <div className="plan-aisle" />}
            <div className="plan-row">
              <span className="rl">{list[0]?.aisle?.code ?? ""}</span>
              {bays.map((bay) => {
                const cells = list.filter((p) => p.bay === bay);
                const slots = [...new Set(cells.map((c) => c.slot))].sort((a, b) => a - b);
                return (
                  <div key={bay} className="plan-bay" title={`${list[0]?.aisle?.code ?? ""}-${bay}`}>
                    {slots.map((slot) => {
                      const column = cells.filter((c) => c.slot === slot);
                      const used = column.filter((c) => occupied.has(c.bin.id)).length;
                      const sel = column.some((c) => c.bin.id === selectedId);
                      const first = column.sort((a, b) => a.shelf - b.shelf)[0]!;
                      return <i key={slot} className={`slot${used === column.length ? " full" : used ? " part" : ""}${sel ? " sel" : ""}`} title={`${first.bin.code.replace(/-\d+-(\d+)$/, "-x-$1")}: ${used}/${column.length}`} onClick={() => onPick(column.find((c) => c.bin.id === selectedId)?.bin ?? first.bin)} />;
                    })}
                    <b>{bay}</b>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The warehouse as the published layout places it (mockup admin-v1/layout, step 2): racks where
 *  they stand, tinted by how full they are, areas and gates; click a rack to select its aisle. */
export function LayoutPlan({ layout, locations, occupied, selectedPath, onPickAisle }: { layout: BundleLayoutOut; locations: readonly BundleLocation[]; occupied: ReadonlySet<string>; selectedPath: string | null; onPickAisle: (aisle: BundleLocation) => void }) {
  const byCode = new Map(locations.map((l) => [l.code, l]));
  const bins = binPositions(locations);
  const fill = (code: string | null) => {
    if (!code) return "rgba(120,130,150,0.25)";
    const aisle = byCode.get(code);
    const list = aisle ? bins.get(aisle.path) ?? [] : [];
    if (list.length === 0) return "rgba(28,126,214,0.18)";
    const used = list.filter((p) => occupied.has(p.bin.id)).length / list.length;
    return `rgba(28,126,214,${0.15 + used * 0.6})`;
  };
  const vb = layout.viewBox ?? [0, 0, 10000, 6000];
  const L = Math.max(vb[2], vb[3]);
  return (
    <svg viewBox={vb.join(" ")} style={{ width: "100%", height: "auto", background: "#fff", borderRadius: 8 }}>
      {layout.areas.map((a) => <g key={a.id}><rect x={a.x} y={a.y} width={a.w} height={a.h} fill="rgba(255,200,87,0.25)" stroke="#c9a227" strokeWidth={L / 900} strokeDasharray={`${L / 120} ${L / 240}`} /><text x={a.x + a.w / 2} y={a.y + a.h / 2} fontSize={L / 70} textAnchor="middle" dominantBaseline="middle" fill="#8a6d00" fontFamily="Arial, sans-serif">{a.label ?? a.role}</text></g>)}
      {layout.gates.map((g) => <rect key={g.id} x={g.x} y={g.y} width={g.w} height={g.h} fill="#1c7ed6" />)}
      {layout.racks.map((r) => {
        const aisle = r.aisleCode ? byCode.get(r.aisleCode) : undefined;
        const on = aisle ? selectedPath === aisle.path || (selectedPath?.startsWith(`${aisle.path}/`) ?? false) : false;
        const vertical = r.h > r.w;
        const list = aisle ? bins.get(aisle.path) ?? [] : [];
        const used = list.filter((p) => occupied.has(p.bin.id)).length;
        return (
          <g key={r.id} onClick={aisle ? () => onPickAisle(aisle) : undefined} style={{ cursor: aisle ? "pointer" : "default" }}>
            <title>{r.aisleCode ? `${r.aisleCode} · ${used}/${list.length}` : ""}</title>
            <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={fill(r.aisleCode)} stroke={on ? "#1c7ed6" : "#5b6878"} strokeWidth={L / (on ? 250 : 900)} />
            {r.aisleCode && <text x={r.x + r.w / 2} y={r.y + r.h / 2} fontSize={L / 75} textAnchor="middle" dominantBaseline="middle" fill="#16202b" fontFamily="Arial, sans-serif" fontWeight={700} transform={vertical ? `rotate(-90 ${r.x + r.w / 2} ${r.y + r.h / 2})` : undefined}>{r.aisleCode}</text>}
          </g>
        );
      })}
    </svg>
  );
}

